/**
 * Raise Panel View for Poker Reborn
 *
 * Panel with slider and preset buttons for selecting raise/bet amount.
 * Opens when player clicks Bet or Raise action button.
 *
 * @see docs/screens/room/RAISE_PANEL.md - Panel specs, slider, presets, data flow
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '@/components/common/Button';
import { Slider } from '@/components/common/Slider';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Panel background */
  panel: {
    width: 280,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 12,
    paddingTop: 20,
    paddingBottom: 16,
    gap: 16,
  },

  /** Amount display */
  amount: {
    fill: 0xffd700,
    fontSize: 28,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Slider style overrides */
  slider: {
    width: 240,
    trackColor: 0x3a3a5e,
    fillColor: 0xff9800,
    thumbColor: 0xffffff,
    thumbHoverColor: 0xdddddd,
    trackHeight: 8,
    thumbRadius: 12,
  },

  /** Preset button */
  preset: {
    width: 55,
    height: 32,
    spacing: 8,
  },
};

// =============================================================================
// HELPERS
// =============================================================================

/** Smart step increment based on value range */
function getStepForRange(range: number): number {
  if (range > 10000) return 100;
  if (range > 1000) return 10;
  if (range > 100) return 5;
  return 1;
}

/** Format chip amount for display */
function formatAmount(amount: number): string {
  return `$${amount.toLocaleString()}`;
}

// =============================================================================
// RAISE PANEL VIEW
// =============================================================================

export class RaisePanelView extends Container {
  private overlay: Graphics;
  private panelBackground: Graphics;
  private amountText: Text;
  private slider: Slider;
  private presetContainer: Container;
  private confirmButton: Button;

  private currentAmount: number;

  /** Callback when raise is confirmed */
  onConfirm: ((amount: number) => void) | null = null;

  /** Callback when panel is cancelled */
  onCancel: (() => void) | null = null;

  constructor(
    minRaise: number,
    maxRaise: number,
    potSize: number,
    confirmLabel = 'Raise',
    screenWidth = 1920,
    screenHeight = 1080,
  ) {
    super();

    this.currentAmount = minRaise;

    const panelW = STYLES.panel.width;
    const { paddingTop, paddingBottom, gap } = STYLES.panel;

    // -- Dismiss overlay (full screen, transparent) --
    this.overlay = new Graphics();
    this.overlay.rect(-screenWidth, -screenHeight, screenWidth * 2, screenHeight * 2);
    this.overlay.fill({ color: 0x000000, alpha: 0.01 });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'default';
    this.overlay.on('pointerdown', () => { this.onCancel?.(); });
    this.addChild(this.overlay);

    // -- Panel background (placeholder, redrawn after layout) --
    this.panelBackground = new Graphics();
    this.panelBackground.eventMode = 'static';
    this.addChild(this.panelBackground);

    // -- Amount display --
    this.amountText = new Text({
      text: formatAmount(this.currentAmount),
      style: STYLES.amount,
    });
    this.amountText.anchor.set(0.5, 0);
    this.addChild(this.amountText);

    // -- Slider (reuse Slider component) --
    const range = maxRaise - minRaise;
    const step = getStepForRange(range);

    this.slider = new Slider({
      width: STYLES.slider.width,
      min: minRaise,
      max: maxRaise,
      value: minRaise,
      step,
      showPercentage: false,
      hideValueText: true,
      style: {
        trackColor: STYLES.slider.trackColor,
        fillColor: STYLES.slider.fillColor,
        thumbColor: STYLES.slider.thumbColor,
        thumbHoverColor: STYLES.slider.thumbHoverColor,
        trackHeight: STYLES.slider.trackHeight,
        thumbRadius: STYLES.slider.thumbRadius,
        labelColor: 0xcccccc,
        valueColor: 0xffffff,
        fontSize: 14,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.slider.onChange = (value) => {
      this.currentAmount = value;
      this.amountText.text = formatAmount(value);
    };
    this.addChild(this.slider);

    // -- Preset buttons --
    this.presetContainer = new Container();
    this.addChild(this.presetContainer);
    this.createPresetButtons(minRaise, maxRaise, potSize);

    // -- Confirm button --
    const confirmW = 80;
    const confirmH = 34;
    this.confirmButton = new Button({
      label: confirmLabel,
      width: confirmW,
      height: confirmH,
      style: {
        ...PRIMARY_BUTTON_STYLE,
        fontSize: 14,
      },
    });
    this.confirmButton.onClick = () => { this.handleConfirm(); };
    this.addChild(this.confirmButton);

    // =========================================================================
    // Dynamic vertical layout (top-down, then center around origin)
    // =========================================================================
    const amountH = STYLES.amount.fontSize;
    const sliderH = STYLES.slider.thumbRadius * 2;
    const presetH = STYLES.preset.height;

    const totalHeight = paddingTop + amountH + gap + sliderH + gap + presetH + gap + confirmH + paddingBottom;
    const halfH = totalHeight / 2;

    // Position each element from top
    let cursorY = -halfH + paddingTop;

    // Amount (anchor 0.5, 0 — centered horizontally, top-aligned)
    this.amountText.y = cursorY;
    cursorY += amountH + gap;

    // Slider (top-left origin)
    this.slider.x = -STYLES.slider.width / 2;
    this.slider.y = cursorY;
    cursorY += sliderH + gap;

    // Preset buttons
    this.presetContainer.y = cursorY;
    cursorY += presetH + gap;

    // Confirm button (top-left origin, center horizontally)
    this.confirmButton.x = -confirmW / 2;
    this.confirmButton.y = cursorY;

    // -- Draw panel background to fit content --
    this.panelBackground.roundRect(-panelW / 2, -halfH, panelW, totalHeight, STYLES.panel.cornerRadius);
    this.panelBackground.fill(STYLES.panel.fill);
    this.panelBackground.setStrokeStyle({ width: 2, color: STYLES.panel.stroke });
    this.panelBackground.stroke();
  }

  /** Show the panel */
  show(): void {
    this.visible = true;
  }

  /** Hide the panel */
  hide(): void {
    this.visible = false;
  }

  /** Get the current value */
  getValue(): number {
    return this.currentAmount;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private createPresetButtons(minRaise: number, maxRaise: number, potSize: number): void {
    const allPresets = [
      { label: 'Min', value: minRaise },
      { label: '1/2', value: Math.floor(potSize / 2) },
      { label: '3/4', value: Math.floor(potSize * 0.75) },
      { label: 'Pot', value: potSize },
    ];

    // Filter out presets that duplicate Min (when potSize is 0 or presets clamp to minRaise)
    const presets: { label: string; value: number }[] = [];
    const seenValues = new Set<number>();

    for (const preset of allPresets) {
      const clamped = Math.max(minRaise, Math.min(maxRaise, preset.value));
      if (!seenValues.has(clamped)) {
        seenValues.add(clamped);
        presets.push({ label: preset.label, value: clamped });
      }
    }

    const { width: buttonW, height: buttonH, spacing } = STYLES.preset;
    const totalWidth = presets.length * buttonW + (presets.length - 1) * spacing;
    const startX = -totalWidth / 2;

    for (let i = 0; i < presets.length; i++) {
      const preset = presets[i]!;

      const button = new Button({
        label: preset.label,
        width: buttonW,
        height: buttonH,
        style: {
          ...SECONDARY_BUTTON_STYLE,
          fontSize: 11,
        },
      });
      button.onClick = () => {
        this.slider.value = preset.value;
        this.currentAmount = this.slider.value;
        this.amountText.text = formatAmount(this.currentAmount);
      };
      button.x = startX + i * (buttonW + spacing);
      this.presetContainer.addChild(button);
    }
  }

  private handleConfirm(): void {
    if (this.onConfirm) {
      this.onConfirm(this.currentAmount);
    }
    this.hide();
  }
}
